import React, { useState, useRef } from 'react';
import styles from './PrintStickerModal.module.css';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';

function PrintStickerModal({ setOpenStickerModal, stickerData }) {
    const stickerRef = useRef();
    const [selectedSize, setSelectedSize] = useState('4x6');
    const [customWidth, setCustomWidth] = useState(4);
    const [customHeight, setCustomHeight] = useState(6);
    const [loading, setLoading] = useState(false);

    const presetSizes = [
        { name: '4x6 inches', value: '4x6', width: 4, height: 6, label: 'Standard Shipping' },
        { name: '4x4 inches', value: '4x4', width: 4, height: 4, label: 'Square Label' },
        { name: '3x2 inches', value: '3x2', width: 3, height: 2, label: 'Small Label' },
        { name: '2x1 inches', value: '2x1', width: 2, height: 1, label: 'Mini Label' },
        { name: 'Custom', value: 'custom', width: customWidth, height: customHeight, label: 'Custom Size' },
    ];

    const getCurrentSize = () => {
        if (selectedSize === 'custom') {
            return { width: customWidth, height: customHeight };
        }
        const preset = presetSizes.find(s => s.value === selectedSize);
        return { width: preset.width, height: preset.height };
    };

    const handlePrintSticker = useReactToPrint({
        contentRef: stickerRef,
        documentTitle: 'Sticker-' + (stickerData?.InvoiceId || 'order'),
        onBeforeGetContent: () => {
            setLoading(true);
        },
        onAfterPrint: () => {
            toast.success('Sticker printed successfully!');
            setLoading(false);
        },
    });

    const currentSize = getCurrentSize();

    return (
        <div className={styles.stickerModalOverlay} onClick={() => setOpenStickerModal(false)}>
            <div className={styles.stickerModalBox} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.stickerHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className={styles.headerIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <path d="M3 9h18" />
                                <path d="M9 21V9" />
                            </svg>
                        </div>
                        <div>
                            <h2 className={styles.headerTitle}>Print Shipping Label</h2>
                            <p className={styles.headerSubtitle}>Order #{stickerData?.InvoiceId || 'N/A'}</p>
                        </div>
                    </div>
                    <button className={styles.closeBtn} onClick={() => setOpenStickerModal(false)}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Size Selection */}
                <div className={styles.sizeSection}>
                    <h3 className={styles.sectionTitle}>Select Label Size</h3>
                    <div className={styles.sizeGrid}>
                        {presetSizes.map((size) => (
                            <div
                                key={size.value}
                                className={`${styles.sizeCard} ${selectedSize === size.value ? styles.sizeCardActive : ''}`}
                                onClick={() => setSelectedSize(size.value)}
                            >
                                <div className={styles.sizeCardLabel}>{size.name}</div>
                                <div className={styles.sizeCardSubtext}>{size.label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Custom Size Inputs */}
                    {selectedSize === 'custom' && (
                        <div className={styles.customSizeInputs}>
                            <div className={styles.inputGroup}>
                                <label>Width (inches)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="12"
                                    step="0.1"
                                    value={customWidth}
                                    onChange={(e) => setCustomWidth(parseFloat(e.target.value) || 1)}
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>Height (inches)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="12"
                                    step="0.1"
                                    value={customHeight}
                                    onChange={(e) => setCustomHeight(parseFloat(e.target.value) || 1)}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Preview */}
                <div className={styles.previewSection}>
                    <h3 className={styles.sectionTitle}>Label Preview</h3>
                    <div className={styles.previewContainer}>
                        <div
                            ref={stickerRef}
                            className={`${styles.stickerContent} ${
                                currentSize.width <= 2 ? styles.stickerSmall :
                                currentSize.width <= 3 ? styles.stickerMedium :
                                styles.stickerLarge
                            }`}
                            style={{
                                width: `${currentSize.width}in`,
                                height: `${currentSize.height}in`,
                            }}
                        >
                            {/* Store Name - only show on medium and large */}
                            {currentSize.width > 2 && (
                                <div className={styles.storeName}>{stickerData?.StoreName || 'Store Name'}</div>
                            )}

                            {/* Order ID with barcode-style */}
                            <div className={styles.orderIdSection}>
                                {currentSize.width > 2 && <div className={styles.orderIdLabel}>ORDER ID</div>}
                                <div className={styles.orderId}>#{stickerData?.InvoiceId || 'N/A'}</div>
                            </div>

                            {/* Customer Info */}
                            <div className={styles.customerSection}>
                                <div className={styles.customerName}>
                                    {stickerData?.Name || 'Customer Name'}
                                </div>
                                <div className={styles.customerPhone}>
                                    {stickerData?.Mobile || 'Phone Number'}
                                </div>
                            </div>

                            {/* Address - hide label on small */}
                            <div className={styles.addressSection}>
                                {currentSize.width > 2 && <div className={styles.addressLabel}>SHIP TO:</div>}
                                <div className={styles.address}>
                                    {stickerData?.Address || 'Delivery Address'}
                                </div>
                            </div>

                            {/* Product Info - only on large labels */}
                            {currentSize.height >= 4 && currentSize.width >= 4 && (
                                <div className={styles.productSection}>
                                    <div className={styles.productName}>
                                        {stickerData?.Products?.[0]?.title || 'Product Name'}
                                    </div>
                                </div>
                            )}

                            {/* Total Amount */}
                            <div className={styles.totalSection}>
                                <span className={styles.totalLabel}>TOTAL:</span>
                                <span className={styles.totalAmount}>৳{stickerData?.TotalPrice || '0'}</span>
                            </div>

                            {/* Payment Method Badge - hide on very small labels */}
                            {currentSize.height >= 1.5 && (
                                <div className={styles.paymentBadge}>
                                    {stickerData?.PaymentType === 'cod' ? 'COD' : 'PREPAID'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className={styles.stickerActions}>
                    <button
                        className={styles.printButton}
                        onClick={handlePrintSticker}
                        disabled={loading}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 6 2 18 2 18 9" />
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                            <rect x="6" y="14" width="12" height="8" />
                        </svg>
                        {loading ? 'Printing...' : 'Print Label'}
                    </button>
                    <button className={styles.cancelButton} onClick={() => setOpenStickerModal(false)}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PrintStickerModal;
